import React, { useState } from "react";
import { CheckCircle2, Circle, AlertCircle, Clock, PauseCircle, ChevronRight, Zap } from "lucide-react";

const STATUS_META = {
  complete: { label: "Complete", cls: "text-emerald-600 bg-emerald-50 border-emerald-200", Icon: CheckCircle2 },
  in_progress: { label: "In Progress", cls: "text-amber-600 bg-amber-50 border-amber-200", Icon: Clock },
  action_needed: { label: "Action Needed", cls: "text-rose-600 bg-rose-50 border-rose-200", Icon: AlertCircle },
  blocked: { label: "Waiting / Blocked", cls: "text-orange-600 bg-orange-50 border-orange-200", Icon: PauseCircle },
  ready_for_review: { label: "Pending Verification", cls: "text-[#006090] bg-sky-50 border-sky-200", Icon: Clock },
  waived: { label: "Waived", cls: "text-slate-500 bg-slate-50 border-slate-200", Icon: CheckCircle2 },
  not_applicable: { label: "Not Applicable", cls: "text-slate-500 bg-slate-50 border-slate-200", Icon: CheckCircle2 },
  not_started: { label: "Not Started", cls: "text-slate-500 bg-slate-50 border-slate-200", Icon: Circle },
};

export function StatusChip({ status }) {
  const m = STATUS_META[status] || STATUS_META.not_started;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${m.cls}`}>
      <m.Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

const PHASE_COLOR = (p) => p.status === "complete" ? "bg-emerald-500 border-emerald-500 text-white"
  : p.is_current ? "bg-[#006090] border-[#006090] text-white"
  : p.status === "in_progress" ? "bg-white border-amber-400 text-amber-600"
  : "bg-white border-slate-300 text-slate-400";

export const nextStepText = (data, candidate) => {
  if (data.configured === false || data.config_status === "no_requirements") return candidate ? "Your recruiter will set up your checklist" : "Configure checklist requirements to begin";
  if (data.config_status === "unmapped" && !data.phases?.some((p) => p.is_current)) return "Program configuration needs review";
  if (data.next_step?.title) return data.waiting_on && data.waiting_on.owner !== "candidate" && !candidate ? `${data.next_step.title} · ${data.waiting_on.message}` : data.next_step.title;
  if (data.waiting_on) return data.waiting_on.message;
  return "All required steps complete";
};

/** Shared assembly-line roadmap. `renderRequirement` lets the parent add controls (internal) vs read-only (candidate). */
export function RoadmapStrip({ data, programLabel, renderRequirement, renderEvidence }) {
  const phases = data?.phases || [];
  const current = phases.find((p) => p.is_current);
  const [open, setOpen] = useState(current?.id || (phases[0] && phases[0].id));

  const openPhase = phases.find((p) => p.id === open) || current;

  return (
    <div className="space-y-5" data-testid="roadmap-strip">
      <div className="rounded-xl bg-[#013A5A] text-white p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-brand-cyan text-xs font-semibold uppercase tracking-wide">Your Path to Agency Ownership</p>
            <h3 className="font-display text-xl font-bold">{programLabel}</h3>
          </div>
          <div className="text-right">
            {data.configured === false
              ? <p className="text-sm font-semibold text-white/80" data-testid="roadmap-progress">Checklist not configured</p>
              : <><p className="text-3xl font-bold" data-testid="roadmap-progress">{data.progress}%</p>
                  <p className="text-xs text-white/70">{data.required_complete}/{data.required_total} required complete</p></>}
          </div>
        </div>
        <div className="mt-3 h-2 bg-white/15 rounded-full overflow-hidden">
          <div className="h-full bg-[#0D9488] rounded-full transition-all" style={{ width: `${data.configured === false ? 0 : data.progress}%` }} />
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
          <div><span className="text-white/60">Current Phase: </span><span className="font-semibold">{current?.name || "—"}</span></div>
          <div data-testid="roadmap-next-step"><span className="text-white/60">Next Step: </span><span className="font-semibold">{nextStepText(data, false)}</span></div>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto tr-scroll pb-2">
        {phases.map((p, i) => (
          <button key={p.id} onClick={() => setOpen(p.id)} data-testid={`roadmap-phase-${p.id}`}
            className={`shrink-0 w-40 text-left rounded-xl border-2 p-3 transition-all ${open === p.id ? "ring-2 ring-[#0D9488]" : ""} ${p.is_current ? "border-[#006090]" : "border-slate-200"} bg-white`}>
            <div className="flex items-center gap-2">
              <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold ${PHASE_COLOR(p)}`}>{i + 1}</span>
              {p.is_current && <span className="text-[10px] font-bold text-[#006090] uppercase" data-testid="you-are-here">You are here</span>}
            </div>
            <p className="mt-2 text-sm font-semibold text-slate-800 leading-tight">{p.name}</p>
            <p className="text-xs text-slate-500 mt-1" data-testid={`roadmap-phase-count-${p.id}`}>{p.empty ? "No requirements configured" : `${p.required_complete}/${p.required_total}${p.pct == null ? "" : ` · ${p.pct}%`}`}{p.status === "awaiting_review" ? " · awaiting review" : ""}</p>
          </button>
        ))}
      </div>

      {openPhase && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="roadmap-phase-detail">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-slate-900">{openPhase.name}</h4>
            <span className="text-xs text-slate-500">{openPhase.required_complete}/{openPhase.required_total} complete{openPhase.pct == null ? "" : ` · ${openPhase.pct}%`}</span>
          </div>
          <div className="divide-y divide-slate-100">
            {(openPhase.requirements || []).length === 0 && <p className="text-sm text-slate-400 py-2">{openPhase.empty ? "No requirements are configured for this phase — it doesn't block progression." : "No requirements shown for this view."}</p>}
            {(openPhase.requirements || []).map((r) => (
              <div key={r.requirement_id} className="py-2.5" data-testid={`roadmap-req-${r.requirement_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 flex items-center gap-2">
                      {r.title}
                      {!r.required && <span className="text-[10px] text-slate-400 border border-slate-200 rounded px-1">optional</span>}
                      {r.auto_reconciled && <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 inline-flex items-center gap-0.5" data-testid={`ms-auto-${r.requirement_id}`}><Zap className="w-2.5 h-2.5" /> Auto-matched</span>}
                    </p>
                    {r.candidate_instructions && <p className="text-xs text-slate-500 mt-0.5">{r.candidate_instructions}</p>}
                    {r.config?.note === "Not Configured" && <p className="text-xs text-amber-600 mt-0.5">Value not configured yet</p>}
                    {r.key_contact_topic && <p className="text-xs text-slate-400 mt-0.5">Help: {r.key_contact_topic}</p>}
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                    {renderRequirement ? renderRequirement(r) : <StatusChip status={r.status} />}
                  </div>
                </div>
                {renderEvidence ? renderEvidence(r) : (r.evidence?.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`roadmap-evidence-${r.requirement_id}`}>
                    {r.evidence.map((e) => (
                      <span key={e.id} className="inline-flex items-center gap-1 text-[11px] bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 text-slate-500">{e.label || "Evidence"}</span>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
